import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

export const DevisModal = ({ trigger, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // FormSpree endpoint - Envoi automatique des emails
      const response = await fetch('https://formspree.io/f/xgoypzej', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Non renseigné',
          service: formData.service || 'Non précisé',
          message: formData.message,
          _subject: 'Nouvelle demande de devis - DMS David Multi Service',
        }),
      });

      if (response.ok) {
        toast.success('Demande envoyée !', {
          description: 'Je vous recontacterai très rapidement.',
        });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setTimeout(() => setOpen(false), 1500);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur d\'envoi', {
        description: 'Une erreur est survenue. Appelez-moi au 07 44 92 12 08',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className={triggerClassName || "bg-green-600 hover:bg-green-700 text-white"}>
            Demander un devis
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Demande de devis gratuit
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Remplissez ce formulaire et je vous recontacterai rapidement pour établir votre devis personnalisé.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom complet *</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Votre nom"
              value={formData.name}
              onChange={handleChange}
              required
              className="border-gray-300 focus:border-green-500 focus:ring-green-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="votre@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="border-gray-300 focus:border-green-500 focus:ring-green-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="06 12 34 56 78"
              value={formData.phone}
              onChange={handleChange}
              className="border-gray-300 focus:border-green-500 focus:ring-green-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="service">Service souhaité</Label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-500 focus:ring-green-500 focus:outline-none"
            >
              <option value="">Sélectionnez un service</option>
              <option value="Petit Bricolage">Petit Bricolage</option>
              <option value="Installation & Aménagement">Installation & Aménagement</option>
              <option value="Jardinage & Espaces Verts">Jardinage & Espaces Verts</option>
              <option value="Assistance Informatique">Assistance Informatique</option>
              <option value="Réparations & Entretien">Réparations & Entretien</option>
              <option value="Plusieurs services">Plusieurs services</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Décrivez votre besoin *</Label>
            <Textarea
              id="message"
              name="message"
              placeholder="Décrivez votre projet en quelques mots..."
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              className="border-gray-300 focus:border-green-500 focus:ring-green-500 resize-none"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Envoi en cours...
              </span>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2 inline" />
                Envoyer ma demande
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
